import React, { useState, useEffect } from 'react';
import { cardsAPI } from '../services/api';

const Cards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCards, setSelectedCards] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const [cardTypes, setCardTypes] = useState([]);
  const [cardNetworks, setCardNetworks] = useState([]);
  const [cardStatuses, setCardStatuses] = useState([]);

  useEffect(() => {
    fetchCards();
    fetchCardTypes();
    fetchCardNetworks();
    fetchCardStatuses();
  }, [currentPage, searchTerm, statusFilter, typeFilter, networkFilter]);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 20,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        card_type: typeFilter || undefined,
        card_network: networkFilter || undefined,
      };
      
      const response = await cardsAPI.getCards(params);
      setCards(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (err) {
      setError('Failed to fetch cards');
      console.error('Error fetching cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardTypes = async () => {
    try {
      const response = await cardsAPI.getCardTypes();
      setCardTypes(response.data);
    } catch (err) {
      console.error('Error fetching card types:', err);
    }
  };

  const fetchCardNetworks = async () => {
    try {
      const response = await cardsAPI.getCardNetworks();
      setCardNetworks(response.data);
    } catch (err) {
      console.error('Error fetching card networks:', err);
    }
  };

  const fetchCardStatuses = async () => {
    try {
      const response = await cardsAPI.getCardStatuses();
      setCardStatuses(response.data);
    } catch (err) {
      console.error('Error fetching card statuses:', err);
    }
  };

  const handleToggleStatus = async (cardId) => {
    try {
      await cardsAPI.toggleCardStatus(cardId);
      showAlert('success', 'Card status updated successfully');
      fetchCards();
    } catch (err) {
      showAlert('error', 'Failed to update card status');
    }
  };

  const handleLockCard = async (cardId) => {
    try {
      await cardsAPI.lockCard(cardId);
      showAlert('success', 'Card locked successfully');
      fetchCards();
    } catch (err) {
      showAlert('error', 'Failed to lock card');
    }
  };

  const handleUnlockCard = async (cardId) => {
    try {
      await cardsAPI.unlockCard(cardId);
      showAlert('success', 'Card unlocked successfully');
      fetchCards();
    } catch (err) {
      showAlert('error', 'Failed to unlock card');
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    
    try {
      await cardsAPI.deleteCard(cardId);
      showAlert('success', 'Card deleted successfully');
      fetchCards();
    } catch (err) {
      showAlert('error', 'Failed to delete card');
    }
  };

  const handleRegenerateCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to regenerate this card? This will create new card number and CVV.')) return;
    
    try {
      const response = await cardsAPI.regenerateCard(cardId);
      const cardData = response.data;
      const successMessage = `Card regenerated successfully!\n\nNew Card Details:\nCard Number: ${cardData.card_number}\nCVV: ${cardData.cvv}\nStatus: ${cardData.status}`;
      alert(successMessage);
      showAlert('success', 'Card regenerated successfully');
      fetchCards();
    } catch (err) {
      showAlert('error', 'Failed to regenerate card');
    }
  };

  const handleBulkAction = async (action, data = {}) => {
    if (selectedCards.length === 0) {
      showAlert('error', 'Please select cards first');
      return;
    }

    try {
      await cardsAPI.bulkUpdateCards({
        card_ids: selectedCards,
        action: action,
        data: data
      });
      showAlert('success', `Bulk ${action} completed successfully`);
      setSelectedCards([]);
      fetchCards();
    } catch (err) {
      showAlert('error', `Failed to perform bulk ${action}`);
    }
  };

  const handleSelectCard = (cardId) => {
    setSelectedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCards.length === cards.length) {
      setSelectedCards([]);
    } else {
      setSelectedCards(cards.map(card => card.id));
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      locked: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading && cards.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alert */}
      {alert.show && (
        <div className={`p-4 rounded-md ${alert.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {alert.message}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-semibold text-gray-900">Cards Management</h1>
          {selectedCards.length > 0 && (
            <span className="text-sm text-gray-500">
              {selectedCards.length} card{selectedCards.length !== 1 ? 's' : ''} selected
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {selectedCards.length > 0 && (
            <>
              <button
                onClick={() => setShowBulkModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
                <span>Bulk Actions</span>
              </button>
              <button
                onClick={() => setSelectedCards([])}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
              >
                Clear Selection
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add New Card</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              {cardStatuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              {cardTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={networkFilter}
              onChange={(e) => setNetworkFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All Networks</option>
              {cardNetworks.map(network => (
                <option key={network.value} value={network.value}>{network.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Cards Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="relative">
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                <input
                  type="checkbox"
                  checked={selectedCards.length === cards.length && cards.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-48">Card</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-40">Holder</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-40">Account</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-24">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-24">Expiry</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-48">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cards.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-2">No cards found</p>
                    <p className="text-sm text-gray-500">Get started by creating a new card or adjust your filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              cards.map((card) => (
              <tr key={card.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap w-12">
                  <input
                    type="checkbox"
                    checked={selectedCards.includes(card.id)}
                    onChange={() => handleSelectCard(card.id)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap min-w-48">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{card.card_name}</div>
                    <div className="text-sm text-gray-500">{card.masked_number}</div>
                    <div className="text-xs text-gray-400">{card.card_type} • {card.card_network}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap min-w-40">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{card.holder_name}</div>
                    <div className="text-sm text-gray-500">{card.user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap min-w-40">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{card.bank_account.account_name}</div>
                    <div className="text-sm text-gray-500">{card.bank_account.account_number}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap min-w-24">
                  {getStatusBadge(card.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 min-w-24">
                  {card.expiry_date}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium min-w-48">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => {setSelectedCard(card); setShowViewModal(true);}}
                      className="text-indigo-600 hover:text-indigo-900 p-1"
                      title="View Details"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {setSelectedCard(card); setShowEditModal(true);}}
                      className="text-blue-600 hover:text-blue-900 p-1"
                      title="Edit Card"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {setSelectedCard(card); setShowTransactionsModal(true);}}
                      className="text-purple-600 hover:text-purple-900 p-1"
                      title="View Transactions"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleRegenerateCard(card.id)}
                      className="text-cyan-600 hover:text-cyan-900 p-1"
                      title="Regenerate Card"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleToggleStatus(card.id)}
                      className={card.status === 'active' ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900 p-1'}
                      title={card.status === 'active' ? 'Suspend Card' : 'Activate Card'}
                    >
                      {card.status === 'active' ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      )}
                    </button>
                    {card.status === 'locked' ? (
                      <button
                        onClick={() => handleUnlockCard(card.id)}
                        className="text-green-600 hover:text-green-900 p-1"
                        title="Unlock Card"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleLockCard(card.id)}
                        className="text-orange-600 hover:text-orange-900 p-1"
                        title="Lock Card"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteCard(card.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                      title="Delete Card"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
            </table>
          </div>
          {/* Scroll indicator */}
          <div className="absolute bottom-0 right-0 bg-gradient-to-l from-white to-transparent w-8 h-8 pointer-events-none"></div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateCardModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchCards();
            showAlert('success', 'Card created successfully');
          }}
          cardTypes={cardTypes}
          cardNetworks={cardNetworks}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCard && (
        <EditCardModal
          card={selectedCard}
          onClose={() => {setShowEditModal(false); setSelectedCard(null);}}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedCard(null);
            fetchCards();
            showAlert('success', 'Card updated successfully');
          }}
        />
      )}

      {/* View Modal */}
      {showViewModal && selectedCard && (
        <ViewCardModal
          card={selectedCard}
          onClose={() => {setShowViewModal(false); setSelectedCard(null);}}
        />
      )}

      {/* Bulk Actions Modal */}
      {showBulkModal && (
        <BulkActionsModal
          selectedCount={selectedCards.length}
          onClose={() => setShowBulkModal(false)}
          onAction={handleBulkAction}
        />
      )}

      {/* Transactions Modal */}
      {showTransactionsModal && selectedCard && (
        <TransactionsModal
          card={selectedCard}
          onClose={() => {setShowTransactionsModal(false); setSelectedCard(null);}}
        />
      )}
    </div>
  );
};

// Create Card Modal Component
const CreateCardModal = ({ onClose, onSuccess, cardTypes, cardNetworks }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    bank_account_id: '',
    card_name: '',
    card_type: 'debit',
    card_network: 'visa',
    holder_name: '',
    daily_atm_limit: 500,
    daily_purchase_limit: 2500,
    monthly_limit: 10000,
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (formData.user_id) {
      fetchBankAccounts(formData.user_id);
    }
  }, [formData.user_id]);

  const fetchUsers = async () => {
    try {
      const response = await cardsAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchBankAccounts = async (userId) => {
    try {
      const response = await cardsAPI.getBankAccounts({ user_id: userId });
      setBankAccounts(response.data);
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await cardsAPI.createCard(formData);
      
      // Show success alert with card details
      const cardData = response.data;
      const successMessage = `Card created successfully!\n\nCard Details:\nCard Number: ${cardData.card_number}\nCVV: ${cardData.cvv}\nExpiry: ${cardData.expiry_month}/${cardData.expiry_year}\nStatus: ${cardData.status}`;
      
      alert(successMessage);
      onSuccess();
    } catch (err) {
      alert('Failed to create card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Create New Card</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
            <select
              value={formData.user_id}
              onChange={(e) => setFormData({...formData, user_id: e.target.value, bank_account_id: ''})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name} - {user.email}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account</label>
            <select
              value={formData.bank_account_id}
              onChange={(e) => setFormData({...formData, bank_account_id: e.target.value})}
              required
              disabled={!formData.user_id}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
            >
              <option value="">Select Account</option>
              {bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name} - {account.account_number}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
              <select
                value={formData.card_type}
                onChange={(e) => setFormData({...formData, card_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {cardTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Network</label>
              <select
                value={formData.card_network}
                onChange={(e) => setFormData({...formData, card_network: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {cardNetworks.map(network => (
                  <option key={network.value} value={network.value}>{network.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Name</label>
            <input
              type="text"
              value={formData.card_name}
              onChange={(e) => setFormData({...formData, card_name: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
            <input
              type="text"
              value={formData.holder_name}
              onChange={(e) => setFormData({...formData, holder_name: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daily ATM Limit</label>
              <input
                type="number"
                value={formData.daily_atm_limit}
                onChange={(e) => setFormData({...formData, daily_atm_limit: parseFloat(e.target.value)})}
                min="0"
                max="5000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daily Purchase Limit</label>
              <input
                type="number"
                value={formData.daily_purchase_limit}
                onChange={(e) => setFormData({...formData, daily_purchase_limit: parseFloat(e.target.value)})}
                min="0"
                max="25000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Limit</label>
              <input
                type="number"
                value={formData.monthly_limit}
                onChange={(e) => setFormData({...formData, monthly_limit: parseFloat(e.target.value)})}
                min="0"
                max="100000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Card'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Card Modal Component
const EditCardModal = ({ card, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    card_name: card.card_name || '',
    holder_name: card.holder_name || '',
    daily_atm_limit: card.limits.daily_atm_limit || 500,
    daily_purchase_limit: card.limits.daily_purchase_limit || 2500,
    monthly_limit: card.limits.monthly_limit || 10000,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await cardsAPI.updateCard(card.id, formData);
      onSuccess();
    } catch (err) {
      alert('Failed to update card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Edit Card</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Name</label>
            <input
              type="text"
              value={formData.card_name}
              onChange={(e) => setFormData({...formData, card_name: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
            <input
              type="text"
              value={formData.holder_name}
              onChange={(e) => setFormData({...formData, holder_name: e.target.value})}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daily ATM Limit</label>
              <input
                type="number"
                value={formData.daily_atm_limit}
                onChange={(e) => setFormData({...formData, daily_atm_limit: parseFloat(e.target.value)})}
                min="0"
                max="5000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daily Purchase Limit</label>
              <input
                type="number"
                value={formData.daily_purchase_limit}
                onChange={(e) => setFormData({...formData, daily_purchase_limit: parseFloat(e.target.value)})}
                min="0"
                max="25000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Limit</label>
              <input
                type="number"
                value={formData.monthly_limit}
                onChange={(e) => setFormData({...formData, monthly_limit: parseFloat(e.target.value)})}
                min="0"
                max="100000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Card'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Card Modal Component
const ViewCardModal = ({ card, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [cardDetails, setCardDetails] = useState(null);

  useEffect(() => {
    fetchCardDetails();
  }, []);

  const fetchCardDetails = async () => {
    try {
      const response = await cardsAPI.getCard(card.id);
      setCardDetails(response.data);
    } catch (err) {
      console.error('Error fetching card details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Card Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {cardDetails && (
          <div className="space-y-6">
            {/* Card Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Card Information</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Name:</strong> {cardDetails.card_name}</div>
                  <div><strong>Type:</strong> {cardDetails.card_type}</div>
                  <div><strong>Network:</strong> {cardDetails.card_network}</div>
                  <div><strong>Full Card Number:</strong> 
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded ml-2">
                      {cardDetails.card_number}
                    </span>
                  </div>
                  <div><strong>Masked Number:</strong> {cardDetails.masked_number}</div>
                  <div><strong>CVV:</strong> 
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded ml-2">
                      {cardDetails.cvv}
                    </span>
                  </div>
                  <div><strong>Holder:</strong> {cardDetails.holder_name}</div>
                  <div><strong>Status:</strong> 
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${
                      cardDetails.status === 'active' ? 'bg-green-100 text-green-800' :
                      cardDetails.status === 'suspended' ? 'bg-red-100 text-red-800' :
                      cardDetails.status === 'locked' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {cardDetails.status?.charAt(0).toUpperCase() + cardDetails.status?.slice(1)}
                    </span>
                  </div>
                  <div><strong>Expiry:</strong> {cardDetails.expiry_month}/{cardDetails.expiry_year}</div>
                  <div><strong>Created:</strong> {new Date(cardDetails.created_at).toLocaleDateString()}</div>
                  <div><strong>Last Updated:</strong> {new Date(cardDetails.updated_at).toLocaleDateString()}</div>
                  {cardDetails.activated_at && (
                    <div><strong>Activated:</strong> {new Date(cardDetails.activated_at).toLocaleDateString()}</div>
                  )}
                  {cardDetails.last_used_at && (
                    <div><strong>Last Used:</strong> {new Date(cardDetails.last_used_at).toLocaleDateString()}</div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>User:</strong> {cardDetails.user.name}</div>
                  <div><strong>Email:</strong> {cardDetails.user.email}</div>
                  <div><strong>Account:</strong> {cardDetails.bank_account.account_name}</div>
                  <div><strong>Account Number:</strong> {cardDetails.bank_account.account_number}</div>
                  <div><strong>Balance:</strong> ${cardDetails.bank_account.balance}</div>
                </div>
              </div>
            </div>

            {/* Security Information */}
            {cardDetails.security && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Security Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div><strong>Failed PIN Attempts:</strong> {cardDetails.security.failed_pin_attempts || 0}</div>
                    {cardDetails.security.locked_until && (
                      <div><strong>Locked Until:</strong> {new Date(cardDetails.security.locked_until).toLocaleString()}</div>
                    )}
                    {cardDetails.security.activation_code && (
                      <div><strong>Activation Code:</strong> 
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded ml-2">
                          {cardDetails.security.activation_code}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Information */}
            {cardDetails.delivery && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Delivery Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div><strong>Status:</strong> {cardDetails.delivery.delivery_status || 'not_shipped'}</div>
                    {cardDetails.delivery.delivery_address && (
                      <div><strong>Address:</strong> {cardDetails.delivery.delivery_address}</div>
                    )}
                  </div>
                  <div>
                    {cardDetails.delivery.tracking_number && (
                      <div><strong>Tracking Number:</strong> {cardDetails.delivery.tracking_number}</div>
                    )}
                    {cardDetails.delivery.shipped_at && (
                      <div><strong>Shipped:</strong> {new Date(cardDetails.delivery.shipped_at).toLocaleDateString()}</div>
                    )}
                    {cardDetails.delivery.delivered_at && (
                      <div><strong>Delivered:</strong> {new Date(cardDetails.delivery.delivered_at).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Design Information */}
            {cardDetails.design && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Design & Physical</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div><strong>Design Theme:</strong> {cardDetails.design.design_theme || 'classic'}</div>
                    <div><strong>Plastic Type:</strong> {cardDetails.design.plastic_type || 'standard'}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Limits */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Card Limits</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-medium">Daily ATM Limit</div>
                  <div>${cardDetails.limits.daily_atm_limit}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-medium">Daily Purchase Limit</div>
                  <div>${cardDetails.limits.daily_purchase_limit}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="font-medium">Monthly Limit</div>
                  <div>${cardDetails.limits.monthly_limit}</div>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Card Settings</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div><strong>International Transactions:</strong> {cardDetails.settings.international_transactions ? 'Enabled' : 'Disabled'}</div>
                  <div><strong>Contactless:</strong> {cardDetails.settings.contactless_enabled ? 'Enabled' : 'Disabled'}</div>
                  <div><strong>Online Transactions:</strong> {cardDetails.settings.online_transactions ? 'Enabled' : 'Disabled'}</div>
                </div>
                <div>
                  <div><strong>ATM Withdrawals:</strong> {cardDetails.settings.atm_withdrawals ? 'Enabled' : 'Disabled'}</div>
                  <div><strong>POS Transactions:</strong> {cardDetails.settings.pos_transactions ? 'Enabled' : 'Disabled'}</div>
                  <div><strong>Transaction Alerts:</strong> {cardDetails.settings.transaction_alerts ? 'Enabled' : 'Disabled'}</div>
                  {cardDetails.settings.low_balance_alerts !== undefined && (
                    <div><strong>Low Balance Alerts:</strong> {cardDetails.settings.low_balance_alerts ? 'Enabled' : 'Disabled'}</div>
                  )}
                  {cardDetails.settings.security_alerts !== undefined && (
                    <div><strong>Security Alerts:</strong> {cardDetails.settings.security_alerts ? 'Enabled' : 'Disabled'}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Metadata */}
            {cardDetails.metadata && Object.keys(cardDetails.metadata).length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Additional Metadata</h4>
                <div className="bg-gray-50 p-3 rounded">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(cardDetails.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            {cardDetails.recent_transactions && cardDetails.recent_transactions.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Recent Transactions</h4>
                <div className="space-y-2">
                  {cardDetails.recent_transactions.slice(0, 5).map((transaction, index) => (
                    <div key={index} className="flex justify-between items-center text-sm py-2 border-b">
                      <div>
                        <div className="font-medium">{transaction.merchant_name}</div>
                        <div className="text-gray-500">{transaction.type} • {transaction.method}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">${transaction.amount}</div>
                        <div className="text-gray-500">{new Date(transaction.processed_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Bulk Actions Modal Component
const BulkActionsModal = ({ selectedCount, onClose, onAction }) => {
  const [action, setAction] = useState('activate');
  const [loading, setLoading] = useState(false);
  const [limitsData, setLimitsData] = useState({
    daily_atm_limit: 500,
    daily_purchase_limit: 2500,
    monthly_limit: 10000
  });
  const [settingsData, setSettingsData] = useState({
    international_transactions: true,
    contactless_enabled: true,
    online_transactions: true,
    atm_withdrawals: true,
    pos_transactions: true,
    transaction_alerts: true,
    low_balance_alerts: true,
    security_alerts: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let data = {};
      if (action === 'update_limits') {
        data = limitsData;
      } else if (action === 'update_settings') {
        data = settingsData;
      }
      
      await onAction(action, data);
      onClose();
    } catch (err) {
      console.error('Bulk action failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Bulk Actions</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4 text-sm text-gray-600">
          {selectedCount} card{selectedCount !== 1 ? 's' : ''} selected
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="activate">Activate Cards</option>
              <option value="suspend">Suspend Cards</option>
              <option value="lock">Lock Cards</option>
              <option value="unlock">Unlock Cards</option>
              <option value="update_limits">Update Limits</option>
              <option value="update_settings">Update Settings</option>
            </select>
          </div>

          {action === 'update_limits' && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Card Limits</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily ATM Limit</label>
                <input
                  type="number"
                  value={limitsData.daily_atm_limit}
                  onChange={(e) => setLimitsData({...limitsData, daily_atm_limit: parseFloat(e.target.value)})}
                  min="0"
                  max="5000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Purchase Limit</label>
                <input
                  type="number"
                  value={limitsData.daily_purchase_limit}
                  onChange={(e) => setLimitsData({...limitsData, daily_purchase_limit: parseFloat(e.target.value)})}
                  min="0"
                  max="25000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Limit</label>
                <input
                  type="number"
                  value={limitsData.monthly_limit}
                  onChange={(e) => setLimitsData({...limitsData, monthly_limit: parseFloat(e.target.value)})}
                  min="0"
                  max="100000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          {action === 'update_settings' && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Card Settings</h4>
              {Object.entries(settingsData).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setSettingsData({...settingsData, [key]: e.target.checked})}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Apply Action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Transactions Modal Component
const TransactionsModal = ({ card, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await cardsAPI.getCardTransactions(card.id);
      setTransactions(response.data.data);
    } catch (err) {
      setError('Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Card Transactions - {card.card_name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-red-600 text-center py-4">{error}</div>
        ) : (
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <div className="text-gray-500 text-center py-8">No transactions found</div>
            ) : (
              <div className="space-y-2">
                {transactions.map((transaction, index) => (
                  <div key={index} className="flex justify-between items-center p-4 border rounded-lg">
                    <div>
                      <div className="font-medium">{transaction.merchant_name || 'Unknown Merchant'}</div>
                      <div className="text-sm text-gray-500">
                        {transaction.type} • {transaction.method} • {transaction.merchant_category}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(transaction.processed_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-medium ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${Math.abs(transaction.amount).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {transaction.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cards; 